/**
 * Wraps a react-select `styles` object with CSS-variable-driven dark mode.
 * Both light and dark values are resolved automatically by the browser at paint time,
 * so the same style object works for both themes.
 *
 * Usage:
 *   styles={withDarkStyles({
 *     control: (p) => ({ ...p, minHeight: '44px', fontSize: '16px' }),
 *     // any extra per-component overrides ...
 *   })}
 */
export function withDarkStyles(extraStyles = {}) {
  const extra = (key, provided, state) =>
    extraStyles[key] ? extraStyles[key](provided, state) : provided;

  return {
    control: (provided, state) => ({
      ...extra('control', provided, state),
      backgroundColor: state.isDisabled ? 'var(--bg-secondary)' : 'var(--bg-card)',
      borderColor: 'var(--border-color)',
    }),
    menu: (provided, state) => ({
      ...extra('menu', provided, state),
      backgroundColor: 'var(--bg-card)',
      borderColor: 'var(--border-color)',
    }),
    menuList: (provided, state) => ({
      ...extra('menuList', provided, state),
      backgroundColor: 'var(--bg-card)',
    }),
    menuPortal: (provided, state) => ({
      ...extra('menuPortal', provided, state),
    }),
    option: (provided, state) => ({
      ...extra('option', provided, state),
      backgroundColor: state.isSelected
        ? '#ccb778'
        : state.isFocused
        ? 'var(--bg-secondary)'
        : 'var(--bg-card)',
      color: state.isSelected ? 'white' : 'var(--text-primary)',
      cursor: 'pointer',
    }),
    singleValue: (provided, state) => ({
      ...extra('singleValue', provided, state),
      color: 'var(--text-primary)',
    }),
    input: (provided, state) => ({
      ...extra('input', provided, state),
      color: 'var(--text-primary)',
    }),
    placeholder: (provided, state) => ({
      ...extra('placeholder', provided, state),
      color: 'var(--text-muted)',
    }),
    indicatorSeparator: (provided, state) => ({
      ...extra('indicatorSeparator', provided, state),
      backgroundColor: 'var(--border-color)',
    }),
    dropdownIndicator: (provided, state) => ({
      ...extra('dropdownIndicator', provided, state),
      color: 'var(--text-muted)',
    }),
    clearIndicator: (provided, state) => ({
      ...extra('clearIndicator', provided, state),
      color: 'var(--text-muted)',
    }),
    multiValue: (provided, state) => ({
      ...extra('multiValue', provided, state),
      backgroundColor: 'var(--bg-secondary)',
    }),
    multiValueLabel: (provided, state) => ({
      ...extra('multiValueLabel', provided, state),
      color: 'var(--text-primary)',
    }),
  };
}
